import { create } from 'zustand';

import { setDisplayCurrency, setViewCurrency } from './format';

const STORAGE_KEY = 'kinshipos.viewCurrency';

const readStored = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Everything the amounts on screen depend on, in one store.
 *
 * It lives here rather than beside the app-bar control because it is not the
 * control's private state: every hook that prints money has to *subscribe* to
 * it, or it will not re-render when the choice changes. That was the second
 * half of this bug — the switch worked, and the plan prices beside it stayed in
 * dollars, because the hook formatting them read the choice as a plain function
 * call that React could not see.
 *
 * The rates live here too, so every setter can apply the conversion
 * synchronously before it publishes new state. Applied from an effect instead,
 * it landed *after* the render it belonged to: the app bar said GBP while every
 * amount in that same render was still formatted in dollars, and nothing
 * re-rendered afterwards to correct them. That is what "it changes once and
 * then stops" actually was.
 */
export interface CurrencyState {
  /** The currency the money is really in: the society's, or the platform's base. */
  own: string;
  /** The currency the viewer chose to read in, if any. */
  code: string | null;
  /** The currency the rates are quoted against. */
  base: string;
  /** Code → rate against `base`. */
  rates: Record<string, number>;
  setOwn: (own: string) => void;
  setRates: (base: string, rates: Record<string, number>) => void;
  set: (code: string | null) => void;
}

/**
 * Rates are quoted against the base currency, so converting out of the currency
 * on screen means dividing by its rate before multiplying by the target's.
 * `null` means "do not convert" — either nothing was chosen, or one of the two
 * currencies has no rate and converting would be a guess dressed as a number.
 */
export function conversionFactor(
  state: Pick<CurrencyState, 'own' | 'code' | 'base' | 'rates'>,
): number | null {
  const { own, code, base, rates } = state;
  if (!code || code === own) return null;
  const rateOf = (c: string) => (c === base ? 1 : rates[c]);
  const from = rateOf(own);
  const to = rateOf(code);
  if (!from || !to) return null;
  return to / from;
}

/** Pushes the store's state into the formatter. Always called before `setState`. */
function syncFormatter(state: Pick<CurrencyState, 'own' | 'code' | 'base' | 'rates'>): void {
  setDisplayCurrency(state.own);
  const factor = conversionFactor(state);
  if (factor === null || !state.code) setViewCurrency(null);
  else setViewCurrency(state.code, factor);
}

export const useCurrencyStore = create<CurrencyState>()((setState, get) => ({
  own: 'INR',
  code: readStored(),
  base: 'INR',
  rates: {},
  setOwn: (own) => {
    syncFormatter({ ...get(), own });
    setState({ own });
  },
  setRates: (base, rates) => {
    syncFormatter({ ...get(), base, rates });
    setState({ base, rates });
  },
  set: (code) => {
    try {
      if (code) localStorage.setItem(STORAGE_KEY, code);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // A browser with storage blocked still switches for this session.
    }
    syncFormatter({ ...get(), code });
    setState({ code });
  },
}));
